import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import Tooltip from "@mui/material/Tooltip";
import { UpiRefundButton } from "./UpiRefundButton";

export const upiTransactionsColumns = [
  {
    field: "id",
    headerName: "Payment ID",
    width: 180,
    renderCell: ({ value }) => (
      <Link
        component={RouterLink}
        to={`/refund-details/${value}`}
        sx={{
          color: 'primary.main',
          textDecoration: 'none',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {value}
      </Link>
    ),
  },
  {
    field: "transactionId",
    headerName: "Trans #",
    width: 80,
    valueGetter: ({ row }) => row.transactionId || row.transaction_id || row.transactionNumber || "-",
    renderCell: ({ value, row }) => {
      const transactionId = row.transactionId || row.transaction_id || row.transactionNumber || value;
      if (transactionId === "-" || !transactionId) {
        return (
          <span style={{ 
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            color: '#666'
          }}>
            {transactionId}
          </span>
        );
      }
      
      return (
        <Link
          component={RouterLink}
          to={`/transactions/${transactionId}`}
          sx={{
            color: '#1976d2',
            textDecoration: 'none',
            fontWeight: 500,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {transactionId}
        </Link>
      );
    },
  },
  {
    field: "amount",
    headerName: "Amount (₹)",
    width: 120,
    type: "number",
    valueGetter: ({ value }) => Number(value || 0) / 100, // Convert from paise to rupees
    valueFormatter: ({ value }) => `₹${Number(value).toFixed(2)}`,
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: ({ value }) => (
      <span style={{ 
        textTransform: 'capitalize',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        backgroundColor: value === 'captured' ? '#e8f5e8' : value === 'refunded' ? '#ffebee' : '#fff3e0',
        color: value === 'captured' ? '#2e7d32' : value === 'refunded' ? '#d32f2f' : '#ed6c02'
      }}>
        {value}
      </span>
    ),
  },
  {
    field: "refund_status",
    headerName: "Refund Status",
    width: 120,
    renderCell: ({ value, row }) => {
      // A wallet top-up funds the wallet, not a session: no refund to show or
      // offer here (refunding it would not take the money out of the wallet).
      if (row.payment_type === "WALLET") {
        return (
          <span style={{ color: '#666', fontStyle: 'italic' }}>
            Wallet top-up
          </span>
        );
      }
      // If there's an actual refund status, show it
      if (value) {
        return (
          <span style={{ 
            textTransform: 'capitalize',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600',
            backgroundColor: value === 'full' ? '#ffebee' : '#fff3e0',
            color: value === 'full' ? '#d32f2f' : '#ed6c02'
          }}>
            {value}
          </span>
        );
      }

      // If no refund status, check transaction stop time from main transactions data
      const stopTime = row.stoptime;
      const transactionId = row.transactionId;
      
      // Only show "Charging in progress" if transaction ID is present and valid
      const hasValidTransactionId = transactionId && transactionId !== "-" && transactionId !== "" && transactionId !== null && transactionId !== undefined;
      
      if (!stopTime || stopTime === null || stopTime === '' || stopTime === undefined) {
        // No stop time - check if transaction ID exists before showing "Charging in progress"
        if (hasValidTransactionId) {
          return (
            <span style={{ 
              color: '#1976d2',
              fontStyle: 'italic',
              fontWeight: '500'
            }}>
              Charging in progress
            </span>
          );
        } else {
          // No transaction ID - show refund button only for Paytm payments
            return (
              <UpiRefundButton paymentId={row.id} />
            );
          
        }
      } else {
        // Session stopped with money left over but no refund recorded here. It
        // may have been refunded by hand on the gateway dashboard, which this
        // system never hears about - so say what is known, not "pending".
        if (Number(row.amount_refunded) > 0) {
          return (
            <Tooltip
              title="Money was left over when this session stopped, but no refund is recorded here. It may have been refunded manually on the payment gateway - check there before refunding."
              placement="top"
            >
              <span style={{
                color: '#ed6c02',
                fontWeight: '500'
              }}>
                Refund not recorded
              </span>
            </Tooltip>
          );
        }
        // Has stop time - full amount used for charging
        return (
          <span style={{ 
            color: '#666',
            fontStyle: 'italic'
          }}>
            No Refund - Full Amount used for Charging
          </span>
        );
      }
    },
  },
  {
    field: "amount_refunded",
    headerName: "Refund Amount",
    width: 140,
    type: "number",
    valueGetter: ({ value }) => Number(value || 0) / 100, // Convert from paise to rupees
    valueFormatter: ({ value }) => {
      if (!value || value === 0) return "-";
      return `₹${Number(value).toFixed(2)}`;
    },
    renderCell: ({ value, row }) => {
      if (!value || value === 0) {
        return <span style={{ color: '#666', fontStyle: 'italic' }}>-</span>;
      }
      return (
        <span style={{ 
          color: '#d32f2f',
          fontWeight: '600'
        }}>
          ₹{Number(value).toFixed(2)}
        </span>
      );
    },
  },
  {
    field: "vpa",
    headerName: "UPI ID",
    minWidth: 200,
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "chargerId",
    headerName: "Charger ID",
    width: 150,
    valueGetter: ({ row }) => row.notes?.chargerId || "-",
  },
  {
    field: "connectorId",
    headerName: "Connector ID",
    width: 120,
    valueGetter: ({ row }) => row.notes?.connectorId || "-",
  },
  {
    field: "method",
    headerName: "Payment Method",
    width: 100,
    valueGetter: ({ value }) => value?.toUpperCase() || "-",
  },
  {
    field: "created_at",
    headerName: "Created At",
    width: 150,
    type: "dateTime",
    valueFormatter: ({ value }) => yearFirstDateTimeFormat(value || ""),
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 200,
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
];
